import React, {
	useContext,
	ReactNode,
	useEffect,
	useState,
	RefAttributes,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocalStorage } from '@mantine/hooks';
import { AppError, ErrorCode, ForeignLanguage, Language } from '@/types/types';
import { Link, LinkProps } from 'react-router-dom';
import { getFileSrc, isAppError } from '@/utils/utilities';
import { Typography } from '@mantine/core';
import parse, { DOMNode, Element, domToReact } from 'html-react-parser';
import StorageKey from '@/configs/storageKey';
import ContentApi, { Content, ContentType } from '@/api/ContentApi';
import LANGUAGES from '@/configs/languages';
import httpClient from '@/api/httpClient';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';
import 'moment/dist/locale/pl';

const errorSlug = 'error.';

export type AppContent = Record<string, Content>;

export interface ContentContext {
	getContent: (
		slug: string,
		options?: {
			links?: Record<
				string,
				LinkProps & React.RefAttributes<HTMLAnchorElement>
			>;
			vars?: Record<string, string | number>;
		}
	) => string;
	language: Language;
	setLanguage: (lang: Language) => void;
	getErrorMessage: (code: AppError['stack']) => string;
	getMutationErrorMessage: (error: unknown) => string;
	getEntityText: <
		TObj extends {
			translations: Record<
				ForeignLanguage,
				Partial<Record<keyof TObj, string>>
			> | null;
		},
	>(
		obj: TObj,
		key: keyof TObj
	) => string;
}

type GetElementOptions = {
	links?: Record<string, LinkProps & RefAttributes<HTMLAnchorElement>>;
	vars?: Record<string, string | number>;
};

const ContentContext = React.createContext<ContentContext>(null!);

export const useContent = () => useContext(ContentContext);

export const ContentProvider = ({ children }: { children: ReactNode }) => {
	const [content, setContent] = useLocalStorage<AppContent>({
		key: StorageKey.CONTENT,
		defaultValue: {},
		getInitialValueInEffect: false,
	});

	const contentQuery = useQuery({
		queryKey: [ContentApi.queryKey],
		queryFn: ContentApi.getAll,
		staleTime: 1000 * 60 * 5,
		select: (content) =>
			content.reduce<AppContent>((acc, curr) => {
				acc[curr.slug] = curr;
				return acc;
			}, {}),
	});

	useEffect(() => {
		if (!contentQuery.data) return;

		setContent(contentQuery.data);
	}, [contentQuery.data]);

	const [language, setLanguage] = useLocalStorage<Language>({
		key: 'language',
		defaultValue: APP_CONFIG.DEFAULT_LANGUAGE,
		getInitialValueInEffect: false,
	});

	const [_, setRefresher] = useState(false);
	const refreshApp = () => setRefresher((prev) => !prev);

	useEffect(() => {
		const code = LANGUAGES[language].code;

		moment.locale(code);
		document.documentElement.setAttribute('lang', code);
		httpClient.defaults.headers['Accept-Language'] = language.toLowerCase();

		refreshApp();
	}, [language]);

	const isDefaultLanguage = language === APP_CONFIG.DEFAULT_LANGUAGE;

	const checkIfExist = (slug: string) => {
		if (!content[slug]) return false;

		if (isDefaultLanguage)
			return content[slug].type === ContentType.IMAGE
				? !!content[slug].image
				: !!content[slug].content;

		if (!content[slug].translations || !content[slug].translations[language])
			return false;

		if (content[slug].type === ContentType.IMAGE)
			return !!content[slug].translations[language]?.image;

		return !!content[slug].translations[language]?.content;
	};

	const getEntityText: ContentContext['getEntityText'] = (obj, key) => {
		if (isDefaultLanguage) return obj[key] as string;
		return !!obj.translations &&
			!!obj.translations[language] &&
			!!obj.translations[language][key]
			? (obj.translations[language][key] as string)
			: (obj[key] as string);
	};

	const getText = (
		slug: string,
		vars: Record<string, string | number> = {}
	) => {
		let text = isDefaultLanguage
			? content[slug].content
			: content[slug].translations[language]?.content;
		text ??= '';

		for (const [variable, value] of Object.entries(vars)) {
			text = text.replaceAll(variable, value.toString());
		}

		return text;
	};

	const getElement = (slug: string, options?: GetElementOptions) => {
		const raw = getText(slug, options?.vars);

		return (
			<Typography>
				{parse(raw, {
					replace: (domNode) => {
						const node = domNode as Element;

						if (
							node.name === 'a' &&
							options?.links &&
							options?.links[node.attribs.href]
						) {
							return (
								<Link {...options.links[node.attribs.href]}>
									{domToReact(node.children as DOMNode[])}
								</Link>
							);
						}
					},
				})}
			</Typography>
		);
	};

	const getImage = (slug: string) => {
		return getFileSrc(
			isDefaultLanguage
				? content[slug].image?.path
				: content[slug].translations[language]?.image?.path
		);
	};

	const getContent: ContentContext['getContent'] = (slug, options) => {
		if (!checkIfExist(slug)) return slug;

		switch (content[slug].type) {
			case ContentType.TEXT:
				return getText(slug, options?.vars);
			case ContentType.ELEMENT:
				return getElement(slug, options) as unknown as string;
			case ContentType.IMAGE:
				return getImage(slug);
			default:
				return slug;
		}
	};

	const codeToMessage = (code: ErrorCode | string) => {
		const isErrorCode = Object.values(ErrorCode).includes(code as ErrorCode);
		if (!isErrorCode) return code;

		const exists = checkIfExist(`${errorSlug}${code}`);
		if (!exists) throw new Error(`ERROR_NOT_FOUND: ${code}`);

		return getText(`${errorSlug}${code}`);
	};

	const getErrorMessage: ContentContext['getErrorMessage'] = (code) => {
		const errorMessage = codeToMessage(code);
		return errorMessage || codeToMessage(ErrorCode.GENERIC);
	};

	const getMutationErrorMessage: ContentContext['getMutationErrorMessage'] = (
		error
	) => {
		if (isAppError(error)) return getErrorMessage(error.stack);
		return getErrorMessage(ErrorCode.GENERIC);
	};

	return (
		<ContentContext
			value={{
				getContent,
				language,
				setLanguage,
				getErrorMessage,
				getMutationErrorMessage,
				getEntityText,
			}}
		>
			{children}
		</ContentContext>
	);
};
